/**
 * Umsetzungsgrad — der EINE Ort, an dem die Atlassian-Ziele stehen.
 *
 * Aufbau:
 *
 *  1. **Dieselbe Architekturübersicht wie `/architecture`** — kein neues
 *     Layout, keine Kopie im Code: `<House basePath="/umsetzungsgrad" goals />`.
 *     Sechs Bahnen, dieselben Querschnitts-Balken, dieselbe Kataster-
 *     Reihenfolge, derselbe Schmalfenster-Fallback, derselbe Dialog. Neu ist
 *     nur die Zeile unter jedem Knoten: `CONUT-nnn · Zieldatum · Status` und,
 *     getrennt davon, der abgeleitete Grad.
 *  2. **Abschnitte für die Ziele ohne Komponente** — Artefakt-Typen,
 *     Mechanismen, Workstreams und der Rest (`repo:maco-roadmap`). Ohne sie
 *     wären 15 der 31 Ziele aus dem Produkt verschwunden, sobald die anderen
 *     Seiten die Ziele verlieren.
 *
 * **Der Umsetzungsgrad ist abgeleitet.** Atlassian liefert zu keinem der 31
 * Ziele einen Fortschritt (`progress` ist durchgehend `null`). Was die Seite
 * zeigt, kommt aus unseren eigenen Daten — Bauzustand je Komponente, Bestand je
 * Artefakt-Typ (`components/goals/grade.ts`). Er steht deshalb in einer eigenen
 * Spalte, trägt an jeder einzelnen Stelle das Wort „abgeleitet" und wird unten
 * in einem Satz hergeleitet. Er ist ein Bau-/Bestandsmaß, kein Zielfortschritt.
 */
import { Card } from "@conuti-das/prince-ui";
import { STATUS_LABEL } from "@/components/GoalBadge";
import { GoalSection, goalOverview, goalSections, formatGoalDate, platformGrade } from "@/components/goals";
import { House } from "@/components/house/House";
import "@/components/goals/goals.css";

export const UMSETZUNGSGRAD_PATH = "/umsetzungsgrad";

/** „29 gefährdet · 2 ausstehend" — die Statuslage in einer Zeile. */
export function statusSentence(byStatus: { status: string; count: number }[]): string {
  return byStatus.map((s) => `${s.count} ${STATUS_LABEL[s.status] ?? s.status}`).join(" · ");
}

export function UmsetzungsgradPage() {
  const overview = goalOverview();
  const sections = goalSections();
  const platform = platformGrade();

  return (
    <div className="page page--lanes">
      <div className="page-header">
        <h2 className="page-title">Umsetzungsgrad</h2>
        <div className="page-subtitle">
          {`${overview.total} Atlassian-Ziele über die Plattform: ${statusSentence(overview.byStatus)}.`}
          {overview.nextDate
            ? ` Nächste Fälligkeit ${formatGoalDate(overview.nextDate)} (${overview.nextCount} ${
                overview.nextCount === 1 ? "Ziel" : "Ziele"
              }).`
            : ""}
          {" Dieselbe Architekturübersicht wie unter Architecture, je Knoten um Ziel," +
            " Zieldatum und Zielstatus ergänzt."}
        </div>
      </div>

      {/* Der Hinweis steht VOR den Zahlen, nicht als Fußnote hinterher: wer den
          ersten Balken sieht, soll schon wissen, woher er kommt. */}
      <Card className="ug-note">
        <strong className="ug-note-lead">Der Umsetzungsgrad ist von uns abgeleitet.</strong>{" "}
        {`Atlassian pflegt zu ${overview.withProgress === 0 ? "keinem" : `nur ${overview.withProgress}`} der ${overview.total} Ziele einen Fortschritt.` +
          " Die Spalte „abgeleitet“ misst deshalb unsere eigenen Daten: bei Komponenten den" +
          " Bauzustand (in Betrieb = 100 %, im Aufbau = 50 %, Stub/geplant = 0 %, extern = nicht" +
          " bewertet), bei Artefakt-Typen den Bestand je erfasstem Stand. Mechanismen, Workstreams" +
          " und Repos ohne Kataster-Eintrag haben in unseren Daten keine Entsprechung und tragen" +
          " sichtbar „nicht ableitbar“." +
          " Der Grad ist ein Bau- und Bestandsmaß, kein Zielfortschritt — er ersetzt die" +
          " Zielpflege in Atlassian nicht."}
        <span className="ug-note-metric">
          {`Über alle Komponenten: ${platform.percent} % (${platform.counted} von ${platform.total} bewertbar)`}
        </span>
      </Card>

      <House basePath={UMSETZUNGSGRAD_PATH} goals />

      {sections.map((section) => (
        <GoalSection key={section.id} section={section} />
      ))}
    </div>
  );
}
